import { get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '~/eds/src/components/Button/Button';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { NumberInput } from '~/eds/src/components/NumberInput/NumberInput';
import { Select } from '~/eds/src/components/Select/Select';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { AuthenticationProvider } from '~/enums/AuthenticationProvider';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { UserManagement } from '~/enums/UserManagement';
import {
  DEFAULT_CLAUSES_NUMBER,
  DEFAULT_XRAY_MODELS_NUMBER,
  getAdminConstants,
  UPAS_CACHE_DELAY_MS,
} from '~/features/admin/constants';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { StorageService } from '~/services/StorageService/StorageService';
import { Nullable } from '~/types/Nullable';

import { Configs, DependecyType } from './types';
import { getFeatureFlagOptions, getFeatureFlags, getTitle } from './utils';

export const ClientDetails = ({
  message,
  isLoading,
  onHide,
  onClick,
}: {
  message?: string;
  isLoading?: boolean;
  onHide: () => void;
  onClick: (configs: Configs, message: string) => void;
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { toast } = useToast();

  const { action, client, configs } = useSelector(
    slices.clients.selectors.selectConfiguration,
  );

  const clientId = client?.id;
  const stringifyClientId = clientId ? clientId.toString() : '';
  const currentDomain = configs.subDomain === client?.subDomain;
  const isLessThanMinClauseCountLimit =
    configs.clauseCountLimit < 5 && configs.clauseCountLimit;

  const isLessThanMinXrayCountLimit =
    configs.xrayPromptModelQuota < 5 && configs.xrayPromptModelQuota;

  const isLessThanMinDomainLength =
    configs.subDomain && configs.subDomain.length < 3;

  const isCreateClient = action === 'add';
  const isEditClient = action === 'edit';

  const isBulkMetadataMultipleTasksEnabled =
    configs.bulkDocumentMetadataMultipleTasksEnabled;

  const isBulkMetadataFileMoveEnabled =
    configs.bulkDocumentMetadataFileMoveEnabled;

  const isBulkMetadataFileRenameEnabled =
    configs.bulkDocumentMetadataFilenameUpdateEnabled;

  const isSuperAdminUserManagementAndAuthProviderEnabled = useFlag(
    FlagType.SuperAdminUserManagementAndAuthProvider,
  );

  const isWorkdayUserManagementSelected =
    isSuperAdminUserManagementAndAuthProviderEnabled &&
    configs.userManagement === UserManagement.Workday;
  const isWorkdayAuthProviderSelected =
    isSuperAdminUserManagementAndAuthProviderEnabled &&
    configs.authenticationProvider === AuthenticationProvider.Workday;
  const isWorkdaySelected =
    isWorkdayUserManagementSelected || isWorkdayAuthProviderSelected;

  const isPartialWorkdaySetting =
    configs.tenantId ||
    configs.tenantEnv ||
    configs.cpsHost ||
    configs.wssHost ||
    configs.galahadHost ||
    configs.homepageHost;

  const isCompleteWorkdaySetting =
    configs.tenantId &&
    configs.tenantEnv &&
    (configs.tenantEnv === 'suv' ? configs.suvId : true) &&
    configs.cpsHost &&
    configs.wssHost &&
    configs.galahadHost &&
    configs.homepageHost;

  const isWorkdaySelectedButIncomplete =
    isWorkdaySelected && !isCompleteWorkdaySetting;

  const isBulkMetadataEnabled =
    isBulkMetadataFileRenameEnabled ||
    isBulkMetadataFileMoveEnabled ||
    isBulkMetadataMultipleTasksEnabled;

  const isSupportAddingCustomerBillingIdEnabled = useFlag(
    FlagType.SupportAddingCustomerBillingId,
  );

  const adjustValuesErrorMessage = intl.formatMessage({
    id: 'ClientDetails.adjustValuesError',
    defaultMessage: 'Please adjust the values',
    description: 'Error message to adjust invalid values',
  });

  const fillRequiredFieldsErrorMessage = intl.formatMessage({
    id: 'ClientDetails.fillRequiredFieldsError',
    defaultMessage: 'Please fill in all required fields',
    description: 'Error message to fill required fields',
  });

  const [featurePrerequisite, setFeaturePrerequisite] = useState<
    Nullable<{
      value: FeatureFlagType;
      label: string;
    }>
  >(null);

  const [featureDependencies, setFeatureDependencies] = useState<
    DependecyType[]
  >([]);

  const [isNameTouched, setIsNameTouched] = useState(false);
  const [isBucketTouched, setIsBucketTouched] = useState(false);
  const [isDomainTouched, setIsDomainTouched] = useState(false);

  const isSuperAdminCreateWorkdayTenantEnabled = useFlag(
    FlagType.SuperAdminCreateWorkdayTenant,
  );
  const enableXrayNewPricingModel = useFlag(
    FlagType.DocumentXrayNewPricingModel,
  );

  const { data: flags } = api.endpoints.getClientFeatureFlags.useQuery(
    {
      clientId: clientId!,
    },
    {
      skip: !clientId,
    },
  );

  const isClauseAffordanceEnabled =
    flags && flags[FlagType.HideClauseAffordance];

  const { data: workdayEnvironments = {} } =
    api.endpoints.getWorkdayEnvironments.useQuery(
      {},
      {
        skip: !isSuperAdminCreateWorkdayTenantEnabled,
      },
    );
  const isTenantConnectivityTestEnabled = useFlag(
    FlagType.TenantConnectivityTest,
  );

  const [testConnectivity, { isFetching: isTestingConnectivity }] =
    api.endpoints.testWorkdayConnectivity.useLazyQuery();

  const [lastConnectivityFailureAt, setLastConnectivityFailureAt] = useState<
    number | null
  >(null);
  const connectivityFailureStorageKey = clientId
    ? `connectivity-test-failed-${clientId}`
    : null;

  const {
    data: algorithmsResults = {
      results: [],
    },
    isLoading: isLoadingAlgorithms,
  } = api.endpoints.getCustomAlgorithmChoices.useQuery({});

  const initialAlgorithms = useMemo(
    () =>
      algorithmsResults.results.map((algorithm: any) => ({
        label: algorithm[1],
        value: algorithm[1],
      })),
    [algorithmsResults],
  );

  const { data: existingClient, isError: isErrorExistingClientName } =
    api.endpoints.getExistingClientBySubdomain.useQuery(
      {
        subdomain: configs.subDomain,
      },
      {
        skip: !configs.subDomain,
      },
    );

  const isDuplicateClientName =
    existingClient &&
    existingClient?.client?.name &&
    !isErrorExistingClientName;

  const { data: automationData, isFetching: isFetchingAutomationData } =
    api.endpoints.getClientClause.useQuery(
      { clientId: stringifyClientId },
      { skip: !configs.id },
    );

  const { data: XrayQuota, isFetching: isFetchingXrayQuota } =
    api.endpoints.getModelsQuotaSummary.useQuery({
      clientId: stringifyClientId,
    });

  const [
    featureDependentWarningModal,
    showFeatureDependentWarningModal,
    hideFeatureDependentWarningModal,
  ] = useModal({
    primaryAction: {
      onClick: () => {
        disableDependencies();
        hideFeatureDependentWarningModal();
      },
      text: intl.formatMessage({
        id: 'ClientDetails.yesProceedButton',
        defaultMessage: 'Yes, proceed',
        description:
          'Confirmation button to proceed with feature dependency removal',
      }),
      variant: 'primary' as const,
    },
    title: intl.formatMessage({
      id: 'ClientDetails.turningOffPrerequisiteTitle',
      defaultMessage: 'Turning Off a Prerequisite',
      description: 'Modal title for turning off a feature prerequisite',
    }),
    children: (
      <Layout direction="column" space={4}>
        {featurePrerequisite && featureDependencies && (
          <Text variant="body">
            <FormattedMessage
              id="ClientDetails.prerequisiteWarningMessage"
              defaultMessage="The function <strong>{prerequisiteLabel}</strong> is a prerequisite of the following function(s): <ul>{dependencyList}</ul> Proceeding will remove all of them together. Are you sure?"
              description="Warning message about removing a prerequisite feature. Example: prerequisiteLabel = 'Advanced Search', dependencyList = '<li>Dashboard</li><li>Reports</li>'"
              values={{
                strong: (chunks) => <strong>{chunks}</strong>,
                prerequisiteLabel: featurePrerequisite.label,
                ul: (chunks) => <ul>{chunks}</ul>,
                dependencyList: featureDependencies.map((dependency) => (
                  <li key={dependency.value}>{dependency.label}</li>
                )),
              }}
            />
          </Text>
        )}
      </Layout>
    ),
  });

  const {
    bulkDocumentMetadataFileMove,
    bulkDocumentMetadataFileRename,
    bulkDocumentMetadataMultipleTasksValue,
    CLIENT_OPTIONS,
  } = getAdminConstants(intl);

  const handleFeatureFlagChange = (items: any) => {
    const enablingBulkDocumentMetadata = items.includes(
      FeatureFlagType.BulkDocumentMetadata,
    );
    const enablingBulkDocumentMetadataFileMove = items.includes(
      FeatureFlagType.BulkDocumentMetadataFileMove,
    );
    const enablingBulkDocumentMetadataFilenameUpdate = items.includes(
      FeatureFlagType.BulkDocumentMetadataFilenameUpdate,
    );
    const enablingBulkDocumentMetadataMultipleTasks = items.includes(
      FeatureFlagType.BulkDocumentMetadataMultipleTasks,
    );

    if (isBulkMetadataEnabled && !enablingBulkDocumentMetadata) {
      setFeaturePrerequisite({
        value: FeatureFlagType.BulkDocumentMetadata,
        label: 'Bulk Metadata Updater',
      });

      const dependencies = [];

      if (isBulkMetadataMultipleTasksEnabled) {
        dependencies.push(bulkDocumentMetadataMultipleTasksValue);
      }

      if (isBulkMetadataFileMoveEnabled) {
        dependencies.push(bulkDocumentMetadataFileMove);
      }

      if (isBulkMetadataFileRenameEnabled) {
        dependencies.push(bulkDocumentMetadataFileRename);
      }

      setFeatureDependencies(dependencies);
      showFeatureDependentWarningModal();

      return;
    }

    if (
      isBulkMetadataFileMoveEnabled &&
      !enablingBulkDocumentMetadataMultipleTasks
    ) {
      setFeaturePrerequisite(bulkDocumentMetadataMultipleTasksValue);
      setFeatureDependencies([
        {
          label: 'Bulk Metadata Updater: Move Files',
          value: FeatureFlagType.BulkDocumentMetadataFileMove as const,
        },
      ]);

      showFeatureDependentWarningModal();
      return;
    }

    const bulkDocumentMetadataMultipleTasksDisabled =
      !enablingBulkDocumentMetadataMultipleTasks &&
      !isBulkMetadataMultipleTasksEnabled;

    const bulkDocumentMetadataFileMoveDisabled =
      !enablingBulkDocumentMetadataFileMove && !isBulkMetadataFileMoveEnabled;

    const bulkDocumentMetadataFilenameUpdateDisabled =
      !enablingBulkDocumentMetadataFilenameUpdate &&
      !isBulkMetadataFileRenameEnabled;

    const shouldAutoAddBulkDocumentMetadataMultipleTasks =
      enablingBulkDocumentMetadata &&
      bulkDocumentMetadataMultipleTasksDisabled &&
      bulkDocumentMetadataFileMoveDisabled &&
      bulkDocumentMetadataFilenameUpdateDisabled;

    if (shouldAutoAddBulkDocumentMetadataMultipleTasks) {
      items.push(FeatureFlagType.BulkDocumentMetadataMultipleTasks);
    }

    dispatch(
      slices.clients.actions.setConfigs({
        ...configs,
        inboundEmailEnabled: items.includes(FeatureFlagType.InboundEmail),
        exportExcelEnabled: items.includes(FeatureFlagType.ExportExcel),
        externalApiEnabled: items.includes(FeatureFlagType.ExternalApi),
        allowClauseRenamingEnabled: items.includes(
          FeatureFlagType.AllowClauseRenaming,
        ),
        esWeightedSearchEnabled: items.includes(
          FeatureFlagType.EsWeightedSearch,
        ),
        automationSmartClauseFullEnabled: items.includes(
          FeatureFlagType.AutomationSmartClauseFull,
        ),
        automationSmartClauseApiEnabled: items.includes(
          FeatureFlagType.AutomationSmartClauseApi,
        ),
        workflowEnabled: items.includes(FeatureFlagType.Workflow),
        ssoEnabled: items.includes(FeatureFlagType.SingleSignOn),
        syncPairWebhookEnabled: items.includes(FeatureFlagType.SyncPairWebhook),
        searchV2Enabled: items.includes(FeatureFlagType.SearchV2),
        documentGroupRevampEnabled: items.includes(
          FeatureFlagType.DocumentGroupRevamp,
        ),
        bulkDocumentMetadataEnabled: enablingBulkDocumentMetadata,
        bulkDocumentMetadataFileMoveEnabled:
          enablingBulkDocumentMetadataFileMove,
        bulkDocumentMetadataFilenameUpdateEnabled:
          enablingBulkDocumentMetadataFilenameUpdate,
        bulkDocumentMetadataMultipleTasksEnabled: items.includes(
          FeatureFlagType.BulkDocumentMetadataMultipleTasks,
        ),
      }),
    );
  };

  const disableDependencies = () => {
    const currentConfig: Partial<Configs> = {};

    switch (featurePrerequisite && featurePrerequisite.value) {
      case FeatureFlagType.BulkDocumentMetadata:
        currentConfig.bulkDocumentMetadataEnabled = false;
        break;
      case FeatureFlagType.BulkDocumentMetadataMultipleTasks:
        currentConfig.bulkDocumentMetadataMultipleTasksEnabled = false;
        break;
      default:
        break;
    }

    featureDependencies.forEach((dependency) => {
      switch (dependency.value) {
        case FeatureFlagType.AutomationSmartClauseFull:
          currentConfig.automationSmartClauseFullEnabled = false;
          break;
        case FeatureFlagType.AutomationSmartClauseApi:
          currentConfig.automationSmartClauseApiEnabled = false;
          break;
        case FeatureFlagType.BulkDocumentMetadataFileMove:
          currentConfig.bulkDocumentMetadataFileMoveEnabled = false;
          break;
        case FeatureFlagType.BulkDocumentMetadataFilenameUpdate:
          currentConfig.bulkDocumentMetadataFilenameUpdateEnabled = false;
          break;
        case FeatureFlagType.BulkDocumentMetadataMultipleTasks:
          currentConfig.bulkDocumentMetadataMultipleTasksEnabled = false;
          break;
        default:
          break;
      }
    });

    dispatch(
      slices.clients.actions.setConfigs({ ...configs, ...currentConfig }),
    );
  };

  useEffect(() => {
    if (!connectivityFailureStorageKey) {
      setLastConnectivityFailureAt(null);
      return;
    }

    const storedFailureAt = StorageService.getItem(
      connectivityFailureStorageKey,
    );
    const parsedFailureAt = storedFailureAt
      ? Number.parseInt(storedFailureAt, 10)
      : NaN;

    if (Number.isNaN(parsedFailureAt)) {
      setLastConnectivityFailureAt(null);
      return;
    }

    const hasCooldownElapsed =
      Date.now() >= parsedFailureAt + UPAS_CACHE_DELAY_MS;
    if (hasCooldownElapsed) {
      StorageService.removeItem(connectivityFailureStorageKey);
      setLastConnectivityFailureAt(null);
      return;
    }

    setLastConnectivityFailureAt(parsedFailureAt);
  }, [connectivityFailureStorageKey]);

  const nextConnectivityAvailableAt = lastConnectivityFailureAt
    ? lastConnectivityFailureAt + UPAS_CACHE_DELAY_MS
    : null;

  const isConnectivityCoolingDown = Boolean(
    nextConnectivityAvailableAt && Date.now() < nextConnectivityAvailableAt,
  );

  const connectivityCooldownTooltip =
    isConnectivityCoolingDown && nextConnectivityAvailableAt
      ? intl.formatMessage(
          {
            id: 'ClientDetails.connectivityCooldownTooltip',
            defaultMessage:
              'Connectivity test is temporarily unavailable due to Workday cache propagation. It will be available after {availableAt}.',
            description:
              'Tooltip shown when connectivity test is disabled due to cache cooldown',
          },
          {
            availableAt: new Date(nextConnectivityAvailableAt).toLocaleString(),
          },
        )
      : undefined;

  const handleTestConnectivity = async () => {
    if (!clientId) {
      return;
    }

    try {
      const result = await testConnectivity({
        pilotClientId: clientId,
      }).unwrap();
      if (!result) {
        throw new Error('Connectivity test returned no response');
      }

      toast({
        message: result.message,
        status: result.success ? 'success' : 'danger',
      });

      if (!result.success) {
        const failureAt = Date.now();
        if (connectivityFailureStorageKey) {
          StorageService.setItem(
            connectivityFailureStorageKey,
            failureAt.toString(),
          );
        }
        setLastConnectivityFailureAt(failureAt);
      }
    } catch {
      toast({
        message: 'Failed to test connectivity. Please try again.',
        status: 'danger',
      });
      const failureAt = Date.now();
      if (connectivityFailureStorageKey) {
        StorageService.setItem(
          connectivityFailureStorageKey,
          failureAt.toString(),
        );
      }
      setLastConnectivityFailureAt(failureAt);
    }
  };

  const testIsConfigDirty = (configs: Configs) => {
    if (isEditClient) {
      return (!configs.clauseCountLimit && configs.clauseCountLimit < 5) ||
        (!configs.xrayPromptModelQuota && configs.xrayPromptModelQuota < 5) ||
        (isPartialWorkdaySetting && !isCompleteWorkdaySetting) ||
        isWorkdaySelectedButIncomplete ||
        isLessThanMinDomainLength ||
        (isDuplicateClientName && !currentDomain) ||
        isLessThanMinClauseCountLimit ||
        isLessThanMinXrayCountLimit
        ? adjustValuesErrorMessage
        : !configs.name ||
            !configs.bucket ||
            !configs.clientType ||
            !configs.subDomain ||
            (isSuperAdminUserManagementAndAuthProviderEnabled &&
              (!configs.userManagement || !configs.authenticationProvider))
          ? fillRequiredFieldsErrorMessage
          : undefined;
    } else if (isCreateClient) {
      return isLessThanMinClauseCountLimit ||
        (isPartialWorkdaySetting && !isCompleteWorkdaySetting) ||
        isWorkdaySelectedButIncomplete ||
        isLessThanMinXrayCountLimit ||
        isLessThanMinDomainLength ||
        (isDuplicateClientName && !currentDomain)
        ? adjustValuesErrorMessage
        : !configs.name ||
            !configs.bucket ||
            !configs.clientType ||
            !configs.subDomain ||
            (isSuperAdminUserManagementAndAuthProviderEnabled &&
              (!configs.userManagement || !configs.authenticationProvider))
          ? fillRequiredFieldsErrorMessage
          : undefined;
    }
  };

  const isDisabled = Boolean(testIsConfigDirty(configs));
  const isDisabledTooltip = testIsConfigDirty(configs);

  if (!action) {
    return null;
  }

  const children = (
    <Layout preset="sections">
      {message && <StatusMessage message={message} status="danger" />}
      <Layout direction="column" spacing={12}>
        <FormField<string, false>
          input={TextInput}
          label={intl.formatMessage({
            id: 'ClientDetails.clientNameLabel',
            defaultMessage: 'Client Name',
            description: 'Label for client name field',
          })}
          name="client-name"
          required
          onChange={(input) => {
            setIsNameTouched(true);
            dispatch(
              slices.clients.actions.setConfigs({
                ...configs,
                name: input ?? '',
              }),
            );
          }}
          value={configs.name}
          inputProps={{
            id: 'client--set-name',
            onBlur: () => setIsNameTouched(true),
          }}
          error={
            isNameTouched && !configs.name
              ? intl.formatMessage({
                  id: 'ClientDetails.clientNameEmptyError',
                  defaultMessage: 'Client name cannot be empty',
                  description: 'Error when client name is empty',
                })
              : undefined
          }
        />
        {isSupportAddingCustomerBillingIdEnabled && (
          <FormField<string, false>
            input={TextInput}
            label={intl.formatMessage({
              id: 'ClientDetails.workdayBillingIdLabel',
              defaultMessage: 'Workday Billing Id',
              description: 'Label for Workday billing ID field',
            })}
            name="workday-billing-id"
            description={intl.formatMessage({
              id: 'ClientDetails.workdayBillingIdDescription',
              defaultMessage:
                'This is necessary for actual customers. The Billing Id comes from the Salesforce Account record. Skip this if you are creating an internal client, like for development purposes, sales demo purposes, or your own personal use.',
              description: 'Description for Workday billing ID field',
            })}
            optional
            onChange={(input) => {
              const trimmedInput = input ? input.trim() : '';
              dispatch(
                slices.clients.actions.setConfigs({
                  ...configs,
                  customerBillingId: trimmedInput,
                }),
              );
            }}
            value={configs.customerBillingId}
            inputProps={{
              id: 'client--set-workday-billing-id',
            }}
          />
        )}
        <Layout direction="row" spacing={8}>
          <FormField<string, false>
            input={TextInput}
            label={intl.formatMessage({
              id: 'ClientDetails.bucketNameLabel',
              defaultMessage: 'Bucket Name',
              description: 'Label for bucket name field',
            })}
            name="bucket-name"
            required
            onChange={(input) => {
              setIsBucketTouched(true);
              const validInput = input
                ? input.replace(/[^a-zA-Z0-9.\-_]/g, '').replace(/\s+/g, '')
                : '';

              dispatch(
                slices.clients.actions.setConfigs({
                  ...configs,
                  bucket: validInput,
                }),
              );
            }}
            value={configs.bucket}
            inputProps={{
              id: 'client--read-only-bucket-name',
              disabled: !isCreateClient,
              onBlur: () => setIsBucketTouched(true),
            }}
            error={
              isBucketTouched && !configs.bucket
                ? intl.formatMessage({
                    id: 'ClientDetails.bucketNameEmptyError',
                    defaultMessage: 'Bucket name cannot be empty',
                    description: 'Error when bucket name is empty',
                  })
                : undefined
            }
          />
          <FormField<string, false>
            input={TextInput}
            label={intl.formatMessage({
              id: 'ClientDetails.awsAccountIdLabel',
              defaultMessage: 'AWS Account Id',
              description: 'Label for AWS account ID field',
            })}
            name="aws-account-id"
            optional
            onChange={(input) => {
              dispatch(
                slices.clients.actions.setConfigs({
                  ...configs,
                  awsAccountId: input,
                }),
              );
            }}
            value={configs.awsAccountId}
            inputProps={{
              id: 'client--set-aws-account-id',
            }}
          />
        </Layout>
        {isSuperAdminUserManagementAndAuthProviderEnabled && (
          <Layout direction="row" spacing={8}>
            <FormField<UserManagement, false>
              input={Select}
              label={intl.formatMessage({
                id: 'ClientDetails.userManagementLabel',
                defaultMessage: 'User Management',
                description: 'Label for user management dropdown',
              })}
              name="user-management"
              required
              onChange={(input) => {
                dispatch(
                  slices.clients.actions.setConfigs({
                    ...configs,
                    userManagement: input || UserManagement.Evisort,
                  }),
                );
              }}
              value={configs.userManagement || UserManagement.Evisort}
              inputProps={{
                id: 'client--set-user-management',
                options: [
                  { label: 'Workday', value: UserManagement.Workday },
                  { label: 'Evisort', value: UserManagement.Evisort },
                ],
                isClearable: false,
              }}
            />
            <FormField<AuthenticationProvider, false>
              input={Select}
              label={intl.formatMessage({
                id: 'ClientDetails.authenticationProviderLabel',
                defaultMessage: 'Authentication Provider',
                description: 'Label for authentication provider dropdown',
              })}
              name="authentication-provider"
              required
              onChange={(input) => {
                dispatch(
                  slices.clients.actions.setConfigs({
                    ...configs,
                    authenticationProvider:
                      input || AuthenticationProvider.Evisort,
                  }),
                );
              }}
              value={
                configs.authenticationProvider || AuthenticationProvider.Evisort
              }
              inputProps={{
                id: 'client--set-authentication-provider',
                options: [
                  { label: 'Workday', value: AuthenticationProvider.Workday },
                  { label: 'Evisort', value: AuthenticationProvider.Evisort },
                ],
                isClearable: false,
              }}
            />
          </Layout>
        )}
        {isSuperAdminCreateWorkdayTenantEnabled && (
          <>
            <Layout direction="row" spacing={8}>
              <FormField<string, false>
                input={TextInput}
                label={intl.formatMessage({
                  id: 'ClientDetails.workdayTenantLabel',
                  defaultMessage: 'Workday Tenant',
                  description: 'Label for Workday tenant field',
                })}
                name="tenant-id"
                required={!!isPartialWorkdaySetting || isWorkdaySelected}
                optional={!isPartialWorkdaySetting && !isWorkdaySelected}
                onChange={(input) => {
                  if (input) {
                    const validInput = input.replace(/[^a-zA-Z0-9_-]/g, '');
                    dispatch(
                      slices.clients.actions.setConfigs({
                        ...configs,
                        tenantId: validInput,
                      }),
                    );
                  } else {
                    dispatch(
                      slices.clients.actions.setConfigs({
                        ...configs,
                        tenantId: '',
                      }),
                    );
                  }
                }}
                value={configs.tenantId}
                inputProps={{
                  id: 'client--set-tenant-id',
                }}
              />
              <FormField<string, false>
                input={Select}
                label={intl.formatMessage({
                  id: 'ClientDetails.workdayTenantEnvironmentLabel',
                  defaultMessage: 'Workday Tenant Environment',
                  description: 'Label for Workday tenant environment field',
                })}
                placeholder=""
                name="tenant-environment"
                required={!!isPartialWorkdaySetting || isWorkdaySelected}
                optional={!isPartialWorkdaySetting && !isWorkdaySelected}
                onChange={(input) => {
                  const env = input ? workdayEnvironments[input] : {};
                  dispatch(
                    slices.clients.actions.setConfigs({
                      ...configs,
                      tenantEnv: input,
                      suvId: input === 'suv' ? get(env, 'suv_id', null) : null,
                      cpsHost: get(env, 'cps_host', ''),
                      wssHost: get(env, 'wss_host', ''),
                      galahadHost: get(env, 'galahad_host', ''),
                      homepageHost: get(env, 'homepage_host', ''),
                    }),
                  );
                }}
                value={configs.tenantEnv}
                inputProps={{
                  id: 'client--set-tenant-environment',
                  options: Object.keys(workdayEnvironments).map((env) => ({
                    label: env,
                    value: env,
                  })),
                  isClearable: true,
                }}
              />
              <FormField<string, false>
                input={TextInput}
                disabled={configs.tenantEnv !== 'suv'}
                label={intl.formatMessage({
                  id: 'ClientDetails.workdaySuvIdLabel',
                  defaultMessage: 'Workday SUV ID',
                  description: 'Label for Workday SUV ID field',
                })}
                name="suv-id"
                required={
                  (!!isPartialWorkdaySetting || isWorkdaySelected) &&
                  configs.tenantEnv === 'suv'
                }
                onChange={(input) => {
                  const validInput = input
                    ? input.replace(/[^a-zA-Z0-9_-]/g, '')
                    : '';
                  dispatch(
                    slices.clients.actions.setConfigs({
                      ...configs,
                      suvId: validInput,
                    }),
                  );
                }}
                value={configs.suvId}
                inputProps={{
                  id: 'client--set-suv-id',
                  placeholder: intl.formatMessage({
                    id: 'ClientDetails.workdaySuvIdPlaceholder',
                    defaultMessage: 'For SUVs only',
                    description:
                      'Placeholder for Workday SUV ID field indicating it is for SUVs only',
                  }),
                }}
              />
            </Layout>

            <Layout direction="row" spacing={8}>
              <FormField<string, false>
                input={TextInput}
                disabled={!configs.tenantEnv}
                label={intl.formatMessage({
                  id: 'ClientDetails.cpsHostLabel',
                  defaultMessage: 'Workday CPS Host',
                  description: 'Label for CPS host field',
                })}
                name="cps-host"
                required={!!isPartialWorkdaySetting || isWorkdaySelected}
                optional={!isPartialWorkdaySetting && !isWorkdaySelected}
                onChange={(input) => {
                  if (input) {
                    const validInput = input.trim();
                    dispatch(
                      slices.clients.actions.setConfigs({
                        ...configs,
                        cpsHost: validInput,
                      }),
                    );
                  } else {
                    dispatch(
                      slices.clients.actions.setConfigs({
                        ...configs,
                        cpsHost: '',
                      }),
                    );
                  }
                }}
                value={configs.cpsHost}
                inputProps={{
                  id: 'client--set-cps-host',
                  placeholder: intl.formatMessage({
                    id: 'ClientDetails.cpsHostPlaceholder',
                    defaultMessage:
                      'Cannot be a placeholder when creating or updating clients',
                    description:
                      'Placeholder for CPS host field indicating it is required and cannot be a placeholder value',
                  }),
                }}
              />
              <FormField<string, false>
                input={TextInput}
                disabled={!configs.tenantEnv}
                label={intl.formatMessage({
                  id: 'ClientDetails.galahadHostLabel',
                  defaultMessage: 'Workday Galahad Host',
                  description: 'Label for Galahad host field',
                })}
                name="galahad-host"
                required={!!isPartialWorkdaySetting || isWorkdaySelected}
                optional={!isPartialWorkdaySetting && !isWorkdaySelected}
                onChange={(input) => {
                  if (input) {
                    const validInput = input.trim();
                    dispatch(
                      slices.clients.actions.setConfigs({
                        ...configs,
                        galahadHost: validInput,
                      }),
                    );
                  } else {
                    dispatch(
                      slices.clients.actions.setConfigs({
                        ...configs,
                        galahadHost: '',
                      }),
                    );
                  }
                }}
                value={configs.galahadHost}
                inputProps={{
                  id: 'client--set-galahad-host',
                  placeholder: intl.formatMessage({
                    id: 'ClientDetails.galahadHostPlaceholder',
                    defaultMessage:
                      'Cannot be a placeholder when creating or updating clients',
                    description:
                      'Placeholder for Galahad host field indicating it is required and cannot be a placeholder value',
                  }),
                }}
              />
            </Layout>

            <Layout direction="row" spacing={8}>
              <FormField<string, false>
                input={TextInput}
                disabled={!configs.tenantEnv}
                label={intl.formatMessage({
                  id: 'ClientDetails.wssHostLabel',
                  defaultMessage: 'Workday WSS Host',
                  description: 'Label for WSS host field',
                })}
                name="wss-host"
                required={!!isPartialWorkdaySetting || isWorkdaySelected}
                optional={!isPartialWorkdaySetting && !isWorkdaySelected}
                onChange={(input) => {
                  if (input) {
                    const validInput = input.trim();
                    dispatch(
                      slices.clients.actions.setConfigs({
                        ...configs,
                        wssHost: validInput,
                      }),
                    );
                  } else {
                    dispatch(
                      slices.clients.actions.setConfigs({
                        ...configs,
                        wssHost: '',
                      }),
                    );
                  }
                }}
                value={configs.wssHost}
                inputProps={{
                  id: 'client--set-wss-host',
                  placeholder: intl.formatMessage({
                    id: 'ClientDetails.wssHostPlaceholder',
                    defaultMessage:
                      'Can be a placeholder when creating or updating clients',
                    description:
                      'Placeholder for WSS host field indicating it can be a placeholder when only registering',
                  }),
                }}
              />
              <FormField<string, false>
                input={TextInput}
                disabled={!configs.tenantEnv}
                label={intl.formatMessage({
                  id: 'ClientDetails.homepageHostLabel',
                  defaultMessage: 'Workday Homepage Host',
                  description: 'Label for Homepage host field',
                })}
                name="homepage-host"
                required={!!isPartialWorkdaySetting || isWorkdaySelected}
                optional={!isPartialWorkdaySetting && !isWorkdaySelected}
                onChange={(input) => {
                  if (input) {
                    const validInput = input.trim();
                    dispatch(
                      slices.clients.actions.setConfigs({
                        ...configs,
                        homepageHost: validInput,
                      }),
                    );
                  } else {
                    dispatch(
                      slices.clients.actions.setConfigs({
                        ...configs,
                        homepageHost: '',
                      }),
                    );
                  }
                }}
                value={configs.homepageHost}
                inputProps={{
                  id: 'client--set-homepage-host',
                  placeholder: intl.formatMessage({
                    id: 'ClientDetails.homepageHostPlaceholder',
                    defaultMessage:
                      'Can be a placeholder when creating or updating clients',
                    description:
                      'Placeholder for Homepage host field indicating it can be a placeholder when only registering',
                  }),
                }}
              />
            </Layout>
            {isTenantConnectivityTestEnabled &&
              isEditClient &&
              isCompleteWorkdaySetting && (
                <Layout>
                  <Button
                    text="Test Connectivity"
                    variant="secondary"
                    disabled={!clientId || isConnectivityCoolingDown}
                    isLoading={isTestingConnectivity}
                    tooltip={connectivityCooldownTooltip}
                    onClick={handleTestConnectivity}
                  />
                </Layout>
              )}
          </>
        )}
        <FormField<string, false>
          input={Select}
          label={intl.formatMessage({
            id: 'ClientDetails.clientTypeLabel',
            defaultMessage: 'What type of Client?',
            description: 'Label for client type selection field',
          })}
          name="client-type"
          required
          onChange={(input) => {
            if (input) {
              dispatch(
                slices.clients.actions.setConfigs({
                  ...configs,
                  clientType: input,
                }),
              );
            }
          }}
          value={configs.clientType}
          inputProps={{
            id: 'client--set-client-type',
            options: CLIENT_OPTIONS,
            isClearable: false,
          }}
        />
        {!isClauseAffordanceEnabled && (
          <FormField<number, false>
            input={NumberInput}
            label={intl.formatMessage({
              id: 'ClientDetails.customClausesNumberLabel',
              defaultMessage:
                'Number of Custom Clauses (Minimum 5, Maximum 1000)',
              description: 'Label for number of custom clauses field',
            })}
            name="number-of-custom-clauses"
            required
            onChange={(input) => {
              dispatch(
                slices.clients.actions.setConfigs({
                  ...configs,
                  clauseCountLimit: input ?? DEFAULT_CLAUSES_NUMBER,
                }),
              );
            }}
            value={configs.clauseCountLimit}
            inputProps={{
              id: 'client--set-number-of-custom-clauses',
              max: 1000,
            }}
            footer={
              !isFetchingAutomationData && automationData
                ? intl.formatMessage(
                    {
                      id: 'ClientDetails.customClausesFooter',
                      defaultMessage:
                        'The client has created {count} custom clauses in this environment.',
                      description:
                        'Footer text showing current custom clauses count. Example: count = 15',
                    },
                    { count: automationData.count },
                  )
                : ''
            }
            error={
              isLessThanMinClauseCountLimit
                ? intl.formatMessage({
                    id: 'ClientDetails.customClausesMinError',
                    defaultMessage:
                      'Number of Custom Clauses should be greater than 5',
                    description: 'Error when custom clauses number is too low',
                  })
                : undefined
            }
          />
        )}
        {enableXrayNewPricingModel && (
          <>
            <FormField<number, false>
              input={NumberInput}
              label={intl.formatMessage({
                id: 'ClientDetails.xrayModelsNumberLabel',
                defaultMessage:
                  'Number of Custom AI Models (Minimum 5, Maximum 1000)',
                description: 'Label for number of Custom AI models field',
              })}
              name="number-of-xray-models"
              required
              onChange={(input) => {
                dispatch(
                  slices.clients.actions.setConfigs({
                    ...configs,
                    xrayPromptModelQuota: input ?? DEFAULT_XRAY_MODELS_NUMBER,
                  }),
                );
              }}
              value={configs.xrayPromptModelQuota}
              inputProps={{
                id: 'client--set-number-of-xray-models',
                max: 1000,
              }}
              footer={
                !isFetchingXrayQuota && XrayQuota
                  ? intl.formatMessage(
                      {
                        id: 'ClientDetails.xrayModelsFooter',
                        defaultMessage:
                          'The client has used {consumedQuota} Custom AI model quota in this environment.',
                        description:
                          'Footer text showing current Custom AI models usage. Example: consumedQuota = 25',
                      },
                      { consumedQuota: XrayQuota.consumed_quota },
                    )
                  : ''
              }
              error={
                isLessThanMinXrayCountLimit
                  ? intl.formatMessage({
                      id: 'ClientDetails.xrayModelsMinError',
                      defaultMessage:
                        'Number of Custom AI Models should be greater than 5',
                      description:
                        'Error when Custom AI models number is too low',
                    })
                  : undefined
              }
            />
          </>
        )}
        <FormField<string, false>
          input={TextInput}
          label={intl.formatMessage({
            id: 'ClientDetails.descriptionLabel',
            defaultMessage: 'Description',
            description: 'Label for description field',
          })}
          name="description"
          optional
          onChange={(input) => {
            dispatch(
              slices.clients.actions.setConfigs({
                ...configs,
                descrip: input ?? '',
              }),
            );
          }}
          value={configs.descrip}
          inputProps={{
            id: 'client--set-description',
          }}
        />
        <FormField<string, false>
          input={TextInput}
          label={intl.formatMessage({
            id: 'ClientDetails.domainLabel',
            defaultMessage: 'Domain',
            description: 'Label for domain field',
          })}
          name="domain"
          required
          onChange={(input) => {
            setIsDomainTouched(true);
            if (input) {
              const validInput = input.replace(/[^a-zA-Z0-9-]/g, '');
              dispatch(
                slices.clients.actions.setConfigs({
                  ...configs,
                  subDomain: validInput,
                }),
              );
            } else {
              dispatch(
                slices.clients.actions.setConfigs({
                  ...configs,
                  subDomain: '',
                }),
              );
            }
          }}
          value={configs.subDomain}
          inputProps={{
            id: 'client--set-domain',
            maxLength: 20,
            onBlur: () => setIsDomainTouched(true),
          }}
          error={
            isDomainTouched && !configs.subDomain
              ? intl.formatMessage({
                  id: 'ClientDetails.domainEmptyError',
                  defaultMessage: 'Domain cannot be empty',
                  description: 'Error when domain is empty',
                })
              : configs.subDomain && isDuplicateClientName && !currentDomain
                ? intl.formatMessage(
                    {
                      id: 'ClientDetails.domainExistsError',
                      defaultMessage:
                        'domain already exists. Client Name: {clientName}, Client Id: {clientId}',
                      description:
                        'Error when domain already exists. Example: clientName = "Test Client", clientId = 123',
                    },
                    {
                      clientName: existingClient?.client.name,
                      clientId: existingClient?.client.id,
                    },
                  )
                : isLessThanMinDomainLength
                  ? intl.formatMessage({
                      id: 'ClientDetails.domainMinLengthError',
                      defaultMessage:
                        'Domain needs to be at least 3 characters',
                      description: 'Error when domain is too short',
                    })
                  : undefined
          }
        />
        <FormField
          input={Select}
          label={intl.formatMessage({
            id: 'ClientDetails.customAlgorithmsLabel',
            defaultMessage: 'Custom Algorithms',
            description: 'Label for custom algorithms field',
          })}
          name="custom-algorithms"
          optional
          onChange={(items) => {
            dispatch(
              slices.clients.actions.setConfigs({
                ...configs,
                customAlgorithms: items,
              }),
            );
          }}
          placeholder={intl.formatMessage({
            id: 'ClientDetails.customAlgorithmsPlaceholder',
            defaultMessage: 'Search for custom algorithms',
            description: 'Placeholder for custom algorithms search',
          })}
          value={configs.customAlgorithms}
          inputProps={{
            isLoading: isLoadingAlgorithms,
            isMulti: true,
            id: 'client--set-custom-algorithms',
            menuPlacement: 'top',
            options: initialAlgorithms,
          }}
        />
        <FormField
          input={Select}
          label={intl.formatMessage({
            id: 'ClientDetails.enableFeaturesLabel',
            defaultMessage: 'Enable Features',
            description: 'Label for enable features field',
          })}
          name="enabled-features"
          optional
          onChange={handleFeatureFlagChange}
          placeholder={intl.formatMessage({
            id: 'ClientDetails.enableFeaturesPlaceholder',
            defaultMessage: 'Search for features',
            description: 'Placeholder for features search',
          })}
          value={getFeatureFlags(configs)}
          inputProps={{
            isMulti: true,
            isClearable: false,
            id: 'client--set-enable-features',
            options: getFeatureFlagOptions(configs, intl),
          }}
        />
      </Layout>
    </Layout>
  );

  const modalProps = {
    isFullPage: true,
    isLoading: isLoadingAlgorithms,
    title: getTitle(action, intl),
    children,
    isVisible: true,
    primaryAction: {
      disabled: isDisabled,
      tooltip: isDisabledTooltip,
      isLoading,
      text: isCreateClient
        ? intl.formatMessage({
            id: 'ClientDetails.createNewClientButton',
            defaultMessage: 'Create new client',
            description: 'Button text for creating new client',
          })
        : intl.formatMessage({
            id: 'ClientDetails.saveUpdateButton',
            defaultMessage: 'Save Update',
            description: 'Button text for saving client update',
          }),
      variant: 'primary' as const,
      onClick: () => {
        const message = isEditClient
          ? intl.formatMessage({
              id: 'ClientDetails.clientUpdatedMessage',
              defaultMessage: 'Client has been updated',
              description: 'Success message when client is updated',
            })
          : intl.formatMessage({
              id: 'ClientDetails.clientCreatedMessage',
              defaultMessage: 'Client has been created',
              description: 'Success message when client is created',
            });

        // TODO : Remove this once other workday teams have changed to use SUV ID
        if (isCompleteWorkdaySetting && !configs.dataCenter) {
          dispatch(
            slices.clients.actions.setConfigs({
              ...configs,
              dataCenter: 'PLACEHOLDER',
            }),
          );
        }
        // END TODO

        onClick(configs, message);
      },
    },
    onHide,
    onCancel: onHide,
  };

  return (
    <>
      <Modal {...modalProps} />
      {featureDependentWarningModal}
    </>
  );
};
